import {useEffect, useId, useRef, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {useAuth} from "base/auth";
import {useMapProvider} from "sisbagal/map";
import {useSisbagalAppConfig} from "sisbagal/provider";
import {useGenerateSisbagalReport} from "sisbagal/map/useGenerateSisbagalReport";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Collapse from "@mui/material/Collapse";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import CircularProgress from "@mui/material/CircularProgress";

import AddIcon from "@mui/icons-material/Add";
import LinkIcon from "@mui/icons-material/Link";
import DeleteIcon from "@mui/icons-material/Delete";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import WaterIcon from "@mui/icons-material/Water";
import InvertColorsIcon from "@mui/icons-material/InvertColors";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

const SANEAMENTO_ELEMENTS = [
    {path: "subaglomeracions", label: "Sistemas colectores"},
    {path: "edars", label: "EDAR", deletable: true},
    {path: "bombeos", label: "Bombeos", deletable: true},
    {path: "tanques", label: "Tanques", deletable: true},
    {path: "colectores", label: "Colectores", deletable: true},
    {path: "vertidos", label: "Vertidos", deletable: true},
    {path: "poligonos", label: "Polígonos", deletable: true},
];

const ABASTECEMENTO_ELEMENTS = [
    {path: "sistemas", label: "Sistemas PMAA"},
    {path: "etaps", label: "ETAP", deletable: true},
    {path: "depositos", label: "Depósitos", deletable: true},
    {path: "captacions", label: "Captacións", deletable: true},
];

const SisbagalQuickActions = ({
    canGenerateReport = false,
    generateMapAsPng,
    getMapAsPngDimensions,
    entityData = {},
}) => {
    const {ROLES, hasRole} = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const {setVisibleSectors} = useSisbagalAppConfig();
    const {getAllLayerProviders} = useMapProvider();
    const {generate} = useGenerateSisbagalReport();

    const [expanded, setExpanded] = useState(true);
    const contentId = useId();

    const [addMenuAnchor, setAddMenuAnchor] = useState(null);
    const [conexionMenuAnchor, setConexionMenuAnchor] = useState(null);
    const [deleteMenuAnchor, setDeleteMenuAnchor] = useState(null);
    const [reportMenuAnchor, setReportMenuAnchor] = useState(null);

    const [pendingReport, setPendingReport] = useState(null);
    const [generatingReport, setGeneratingReport] = useState(false);

    // Keep a reference to the latest generate function so the deferred report
    // generation always works with the current layer state.
    const generateRef = useRef(generate);
    generateRef.current = generate;
    const reportArgsRef = useRef({generateMapAsPng, getMapAsPngDimensions, entityData});
    reportArgsRef.current = {generateMapAsPng, getMapAsPngDimensions, entityData};

    // Layers belonging to the sectors requested in the pending report.
    const reportSectors = pendingReport ? pendingReport.sectors : null;
    const reportTargetProviders = reportSectors
        ? getAllLayerProviders().filter(
              provider =>
                  provider.sector && reportSectors.includes(provider.sector)
          )
        : [];
    const reportLayersLoaded = reportTargetProviders.every(
        provider => !provider.loading
    );

    // Apply the requested sector/layer selection when a report is requested.
    useEffect(() => {
        if (!pendingReport) {
            return;
        }
        setVisibleSectors(pendingReport.sectors);
        getAllLayerProviders().forEach(provider => {
            if (provider.sector) {
                provider.showLayer(
                    pendingReport.sectors.includes(provider.sector)
                );
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingReport]);

    // Once the selected layers are loaded, generate the report with the
    // updated map and layer selection. A longer fallback delay avoids waiting
    // forever if a layer fails to finish loading.
    useEffect(() => {
        if (!pendingReport) {
            return;
        }
        let cancelled = false;
        const delay = reportLayersLoaded ? 800 : 4000;
        const timer = setTimeout(async () => {
            if (cancelled) {
                return;
            }
            setGeneratingReport(true);
            await generateRef.current(reportArgsRef.current);
            if (cancelled) {
                return;
            }
            setGeneratingReport(false);
            setPendingReport(null);
        }, delay);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingReport, reportLayersLoaded]);

    // Detect the current context (concello or aglomeración) from the URL.
    // Handles both the detail page (/concellos/:codigo) and the info panel
    // opened from the main map (/concellos/info/concellos/:codigo).
    const pathname = location.pathname;
    const concelloInfoMatch = pathname.match(/^\/concellos\/info\/concellos\/([^/]+)/);
    const concelloDetailMatch = pathname.match(/^\/concellos\/(?!info\/)([^/]+)/);
    const aglomeracionInfoMatch = pathname.match(
        /^\/aglomeracions\/info\/aglomeracions\/([^/]+)/
    );
    const aglomeracionDetailMatch = pathname.match(
        /^\/aglomeracions\/(?!info\/)([^/]+)/
    );

    let basePath = null;
    let isConcello = false;
    if (concelloInfoMatch) {
        basePath = `/concellos/${concelloInfoMatch[1]}`;
        isConcello = true;
    } else if (concelloDetailMatch) {
        basePath = `/concellos/${concelloDetailMatch[1]}`;
        isConcello = true;
    } else if (aglomeracionInfoMatch) {
        basePath = `/aglomeracions/${aglomeracionInfoMatch[1]}`;
        isConcello = false;
    } else if (aglomeracionDetailMatch) {
        basePath = `/aglomeracions/${aglomeracionDetailMatch[1]}`;
        isConcello = false;
    }

    const canEdit =
        hasRole(ROLES.ADMINISTRADOR) || hasRole(ROLES.XESTOR) || hasRole(ROLES.EDITOR);

    if (!basePath) {
        return null;
    }

    const sectors = [
        {
            name: "Saneamento",
            icon: <WaterIcon fontSize="small" />,
            elements: SANEAMENTO_ELEMENTS,
        },
        ...(isConcello
            ? [
                  {
                      name: "Abastecemento",
                      icon: <InvertColorsIcon fontSize="small" />,
                      elements: ABASTECEMENTO_ELEMENTS,
                  },
              ]
            : []),
    ];

    const conexiones = [
        {
            path: "saneam-conexions",
            label: "Saneamento",
            icon: <WaterIcon fontSize="small" />,
        },
        ...(isConcello
            ? [
                  {
                      path: "abast-conexions",
                      label: "Abastecemento",
                      icon: <InvertColorsIcon fontSize="small" />,
                  },
              ]
            : []),
    ];

    const handleNavigate = (path, options = null) => {
        navigate(`${basePath}/${path}`, options || undefined);
    };

    const reportOptions = [
        {
            key: "completo",
            label: "Completo",
            sectors: ["saneamento", "abastecemento"],
        },
        {
            key: "saneamento",
            label: "Saneamento",
            sectors: ["saneamento"],
        },
        ...(isConcello
            ? [
                  {
                      key: "abastecemento",
                      label: "Abastecemento",
                      sectors: ["abastecemento"],
                  },
              ]
            : []),
    ];

    const handleSelectReport = sectors => {
        setReportMenuAnchor(null);
        setPendingReport({sectors, token: Date.now()});
    };

    const renderSectorMenuItems = (onSelect, suffix = "", filterFn = null) =>
        sectors.map(sector => {
            const elements = filterFn
                ? sector.elements.filter(filterFn)
                : sector.elements;
            if (elements.length === 0) {
                return null;
            }
            return (
                <Box key={sector.name}>
                    <MenuItem disabled>
                        <ListItemIcon>{sector.icon}</ListItemIcon>
                        <ListItemText>{sector.name}</ListItemText>
                    </MenuItem>
                    {elements.map(element => (
                        <MenuItem
                            key={element.path}
                            onClick={() => {
                                onSelect(`${element.path}${suffix}`);
                            }}
                        >
                            <ListItemText inset>{element.label}</ListItemText>
                        </MenuItem>
                    ))}
                </Box>
            );
        });

    return (
        <Box>
            <ButtonBase
                onClick={() => setExpanded(value => !value)}
                aria-expanded={expanded}
                aria-controls={contentId}
                sx={{
                    width: "100%",
                    minHeight: 50,
                    px: 1,
                    gap: 1,
                    justifyContent: "flex-start",
                    bgcolor: "secondary.light",
                    borderBottom: "1px solid",
                    borderColor: "secondary.main",
                }}
            >
                <Typography variant="h6" sx={{flex: 1, textAlign: "left"}}>
                    Accións rápidas
                </Typography>
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </ButtonBase>
            <Collapse in={expanded} id={contentId}>
                <Stack sx={{px: 1, py: 1, gap: 1}}>
                    {canEdit && (
                        <>
                            <Button
                                variant="outlined"
                                size="small"
                                fullWidth
                                startIcon={<AddIcon />}
                                endIcon={<ExpandMoreIcon />}
                                onClick={event => setAddMenuAnchor(event.currentTarget)}
                            >
                                Engadir novos elementos
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                fullWidth
                                startIcon={<LinkIcon />}
                                endIcon={<ExpandMoreIcon />}
                                onClick={event =>
                                    setConexionMenuAnchor(event.currentTarget)
                                }
                            >
                                Crear conexións
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                fullWidth
                                startIcon={<DeleteIcon />}
                                endIcon={<ExpandMoreIcon />}
                                onClick={event =>
                                    setDeleteMenuAnchor(event.currentTarget)
                                }
                            >
                                Borrar existente
                            </Button>
                        </>
                    )}
                    {canGenerateReport && (
                        <Button
                            variant="outlined"
                            size="small"
                            fullWidth
                            startIcon={
                                generatingReport ? (
                                    <CircularProgress size={16} />
                                ) : (
                                    <PictureAsPdfOutlinedIcon />
                                )
                            }
                            endIcon={<ExpandMoreIcon />}
                            disabled={generatingReport}
                            onClick={event =>
                                setReportMenuAnchor(event.currentTarget)
                            }
                        >
                            Acceso a informes
                        </Button>
                    )}
                </Stack>
            </Collapse>

            <Menu
                anchorEl={addMenuAnchor}
                open={Boolean(addMenuAnchor)}
                onClose={() => setAddMenuAnchor(null)}
            >
                {renderSectorMenuItems(path => {
                    handleNavigate(`${path}/new`);
                    setAddMenuAnchor(null);
                })}
            </Menu>

            <Menu
                anchorEl={conexionMenuAnchor}
                open={Boolean(conexionMenuAnchor)}
                onClose={() => setConexionMenuAnchor(null)}
            >
                {conexiones.map(conexion => (
                    <MenuItem
                        key={conexion.path}
                        onClick={() => {
                            handleNavigate(conexion.path);
                            setConexionMenuAnchor(null);
                        }}
                    >
                        <ListItemIcon>{conexion.icon}</ListItemIcon>
                        <ListItemText>{conexion.label}</ListItemText>
                    </MenuItem>
                ))}
            </Menu>

            <Menu
                anchorEl={deleteMenuAnchor}
                open={Boolean(deleteMenuAnchor)}
                onClose={() => setDeleteMenuAnchor(null)}
            >
                {renderSectorMenuItems(
                    path => {
                        handleNavigate(path, {state: {deleteMode: true}});
                        setDeleteMenuAnchor(null);
                    },
                    "",
                    element => element.deletable
                )}
            </Menu>

            <Menu
                anchorEl={reportMenuAnchor}
                open={Boolean(reportMenuAnchor)}
                onClose={() => setReportMenuAnchor(null)}
            >
                {reportOptions.map(option => (
                    <MenuItem
                        key={option.key}
                        onClick={() => handleSelectReport(option.sectors)}
                    >
                        <ListItemText>{option.label}</ListItemText>
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
};

export default SisbagalQuickActions;
